import React, { useMemo, useState } from 'react';
import {
  Card,
  CardBody,
  Typography,
} from '@material-tailwind/react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
  CartesianGrid, ScatterChart, Scatter, ZAxis, Cell, PieChart, Pie,
} from 'recharts';

const NUMBER_FMT = new Intl.NumberFormat('en-US');
const fmtNum = (v) => (v != null && !isNaN(v) ? NUMBER_FMT.format(Math.round(v)) : '0');
const fmtPct = (v) => (v != null && !isNaN(v) ? `${(v * 100).toFixed(1)}%` : '0%');

const LISTING_COLORS = [
  '#3b82f6', '#f59e0b', '#059669', '#8b5cf6', '#ef4444',
  '#0891b2', '#d946ef', '#84cc16', '#f97316', '#6366f1',
  '#14b8a6', '#e11d48', '#a855f7', '#22d3ee', '#facc15',
];

const PIE_COLORS = ['#3b82f6', '#f59e0b', '#059669', '#8b5cf6', '#ef4444', '#0891b2', '#d946ef', '#84cc16'];

const getLabel = (d) => d.reg || `${d.year || ''} ${(d.model || '').substring(0, 15)}`;

// Get true DOM for a listing
function getDom(d) {
  return d.relist_true_dom || d.days_aging || 0;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-3 text-sm max-w-xs">
      <div className="font-semibold text-gray-700 truncate">{label}</div>
      {d?.model && <div className="text-gray-500 text-xs mb-1">{d.model}</div>}
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: entry.color || entry.fill }} />
          <span className="text-gray-500">{entry.name}:</span>
          <span className="font-medium">{fmtNum(entry.value)}</span>
        </div>
      ))}
    </div>
  );
};

const ScatterTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-3 text-sm">
      <div className="font-semibold text-gray-700 mb-1">{d.label}</div>
      <div className="text-gray-500">Days on Market: <strong>{d.x}</strong></div>
      <div className="text-gray-500">Total Views: <strong>{fmtNum(d.y)}</strong></div>
      <div className="text-gray-500">Impressions: <strong>{fmtNum(d.impressions)}</strong></div>
    </div>
  );
};

function WebStatsCharts({ filteredListings, hasActiveFilters, modelBenchmarks = {} }) {
  const [chartsExpanded, setChartsExpanded] = useState(true);

  // Only show charts when filters are active and there are listings to compare
  const showCharts = hasActiveFilters && filteredListings.length > 0 && filteredListings.length <= 50;

  // ─── Chart 1: Views by Listing (horizontal bar, ranked) ────────────────
  const viewsBarData = useMemo(() => {
    if (!showCharts) return [];
    return [...filteredListings]
      .sort((a, b) => (b.details_views || 0) - (a.details_views || 0))
      .slice(0, 20)
      .map((d) => ({
        name: getLabel(d),
        model: d.model,
        Views: d.details_views || 0,
        listing_id: d.listing_id,
      }));
  }, [filteredListings, showCharts]);

  // ─── Chart 2: CTR by Listing (horizontal bar) ─────────────────────────
  const ctrBarData = useMemo(() => {
    if (!showCharts) return [];
    return [...filteredListings]
      .sort((a, b) => (b.click_through_rate || 0) - (a.click_through_rate || 0))
      .slice(0, 20)
      .map((d) => ({
        name: getLabel(d),
        model: d.model,
        CTR: d.click_through_rate || 0,
        listing_id: d.listing_id,
      }));
  }, [filteredListings, showCharts]);

  // ─── Chart 3: Days on Market vs Views (scatter) ───────────────────────
  const scatterData = useMemo(() => {
    if (!showCharts) return [];
    return filteredListings.map((d, i) => ({
      x: getDom(d),
      y: d.relist_count > 1 ? (d.relist_cycle_views || d.details_views) : (d.details_views || 0),
      impressions: d.relist_count > 1 ? (d.relist_cycle_impressions || d.impressions) : (d.impressions || 0),
      label: getLabel(d),
      color: LISTING_COLORS[i % LISTING_COLORS.length],
      listing_id: d.listing_id,
    }));
  }, [filteredListings, showCharts]);

  // ─── Chart 4: Views by Model (pie/donut) ──────────────────────────────
  const modelPieData = useMemo(() => {
    if (!showCharts) return [];
    const byModel = {};
    filteredListings.forEach((d) => {
      const model = d.model || 'Unknown';
      byModel[model] = (byModel[model] || 0) + (d.details_views || 0);
    });
    return Object.entries(byModel)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredListings, showCharts]);

  // ─── Chart 5: Engagement by Listing (stacked bar) ─────────────────────
  const engagementData = useMemo(() => {
    if (!showCharts) return [];
    return [...filteredListings]
      .filter((d) => (d.total_clicks_to_call || 0) + (d.email_leads || 0) + (d.total_watchlists || 0) > 0)
      .sort((a, b) => {
        const aTotal = (a.total_clicks_to_call || 0) + (a.email_leads || 0) + (a.total_watchlists || 0);
        const bTotal = (b.total_clicks_to_call || 0) + (b.email_leads || 0) + (b.total_watchlists || 0);
        return bTotal - aTotal;
      })
      .slice(0, 20)
      .map((d) => ({
        name: getLabel(d),
        Calls: d.total_clicks_to_call || 0,
        Emails: d.email_leads || 0,
        Watchlists: d.total_watchlists || 0,
      }));
  }, [filteredListings, showCharts]);

  // ─── Chart 6: DOM vs Model Benchmark ────────────────────────────────
  const domBenchmarkData = useMemo(() => {
    if (!showCharts || !Object.keys(modelBenchmarks).length) return [];
    const active = filteredListings.filter((d) => (d.status || '').toUpperCase() === 'ACTIVE' && getDom(d));
    return active
      .map((d) => {
        const model = (d.model || '').toUpperCase().trim();
        const bm = modelBenchmarks[model];
        if (!bm) return null;
        const dom = getDom(d);
        return {
          name: getLabel(d),
          model: d.model,
          'Days on Market': dom,
          'Avg DOM (Model)': bm.avgDom,
          'Sales Count': bm.salesCount,
          delta: dom - bm.avgDom,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.delta - a.delta)
      .slice(0, 20);
  }, [filteredListings, modelBenchmarks, showCharts]);

  // ─── Chart 7: Lead Engagement by Model ─────────────────────────────────
  const engagementByModelData = useMemo(() => {
    if (!showCharts) return [];
    const byModel = {};
    filteredListings.forEach((d) => {
      const model = d.model || 'Unknown';
      if (!byModel[model]) byModel[model] = { Calls: 0, Emails: 0, Watchlists: 0, count: 0 };
      byModel[model].Calls += d.total_clicks_to_call || 0;
      byModel[model].Emails += d.email_leads || 0;
      byModel[model].Watchlists += d.total_watchlists || 0;
      byModel[model].count++;
    });
    return Object.entries(byModel)
      .map(([model, data]) => ({
        name: model,
        Calls: data.Calls,
        Emails: data.Emails,
        Watchlists: data.Watchlists,
        listings: data.count,
      }))
      .filter((d) => d.Calls + d.Emails + d.Watchlists > 0)
      .sort((a, b) => (b.Calls + b.Emails + b.Watchlists) - (a.Calls + a.Emails + a.Watchlists));
  }, [filteredListings, showCharts]);

  // ─── Aggregate stats ──────────────────────────────────────────────────
  const aggStats = useMemo(() => {
    if (!showCharts) return null;
    const active = filteredListings.filter((d) => (d.status || '').toUpperCase() === 'ACTIVE').length;
    const sold = filteredListings.length - active;
    const totalViews = filteredListings.reduce((s, d) => s + (d.details_views || 0), 0);
    const avgViews = totalViews / filteredListings.length;
    const topListing = filteredListings.reduce((best, d) => (d.details_views || 0) > (best.details_views || 0) ? d : best, filteredListings[0]);
    const bottomListing = filteredListings.reduce((worst, d) => (d.details_views || 0) < (worst.details_views || 0) ? d : worst, filteredListings[0]);
    return { active, sold, totalViews, avgViews, topListing, bottomListing };
  }, [filteredListings, showCharts]);

  if (!showCharts) return null;

  const chartHeight = 220;
  const barHeight = Math.max(180, Math.min(filteredListings.length * 28, 400));

  return (
    <div className="mb-4">
      <button
        onClick={() => setChartsExpanded(!chartsExpanded)}
        className="flex items-center gap-2 mb-3 text-sm font-medium text-gray-600 hover:text-gray-800"
      >
        <svg className={`h-4 w-4 transition-transform ${chartsExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
        Listing Comparison ({filteredListings.length} listings)
      </button>

      {chartsExpanded && (
        <>
          {/* Quick aggregate stats */}
          {aggStats && (
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="px-3 py-1.5 rounded-lg bg-green-50 border border-green-200">
                <span className="text-xs text-green-600">{aggStats.active} Active</span>
              </div>
              {aggStats.sold > 0 && (
                <div className="px-3 py-1.5 rounded-lg bg-red-50 border border-red-200">
                  <span className="text-xs text-red-600">{aggStats.sold} Sold/Expired</span>
                </div>
              )}
              <div className="px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200">
                <span className="text-xs text-blue-700">Avg Views: {fmtNum(aggStats.avgViews)}</span>
              </div>
              <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
                <span className="text-xs text-gray-600">Top: {getLabel(aggStats.topListing)} ({fmtNum(aggStats.topListing.details_views)})</span>
              </div>
              <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
                <span className="text-xs text-gray-600">Bottom: {getLabel(aggStats.bottomListing)} ({fmtNum(aggStats.bottomListing.details_views)})</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Chart 1: Views by Listing */}
            {viewsBarData.length > 0 && (
              <Card className="shadow-sm">
                <CardBody className="p-4">
                  <Typography variant="small" className="font-semibold text-gray-700 mb-1">
                    Total Views by Listing
                  </Typography>
                  <Typography variant="small" className="text-gray-500 text-xs mb-3">
                    Ranked by cumulative detail views — identifies top and bottom performers
                  </Typography>
                  <ResponsiveContainer width="100%" height={barHeight}>
                    <BarChart data={viewsBarData} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} interval={0} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="Views" radius={[0, 4, 4, 0]}>
                        {viewsBarData.map((_, i) => (
                          <Cell key={i} fill={LISTING_COLORS[i % LISTING_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            )}

            {/* Chart 2: CTR by Listing */}
            {ctrBarData.length > 0 && (
              <Card className="shadow-sm">
                <CardBody className="p-4">
                  <Typography variant="small" className="font-semibold text-gray-700 mb-1">
                    Click-Through Rate by Listing
                  </Typography>
                  <Typography variant="small" className="text-gray-500 text-xs mb-3">
                    Higher CTR = more compelling in search — low CTR may need better photos or pricing
                  </Typography>
                  <ResponsiveContainer width="100%" height={barHeight}>
                    <BarChart data={ctrBarData} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => fmtPct(v)} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} interval={0} />
                      <Tooltip content={<CustomTooltip />} formatter={(v) => fmtPct(v)} />
                      <Bar dataKey="CTR" radius={[0, 4, 4, 0]}>
                        {ctrBarData.map((_, i) => (
                          <Cell key={i} fill={LISTING_COLORS[i % LISTING_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            )}

            {/* Chart 3: Days on Market vs Views (scatter) */}
            {scatterData.length > 1 && (
              <Card className="shadow-sm">
                <CardBody className="p-4">
                  <Typography variant="small" className="font-semibold text-gray-700 mb-1">
                    Days on Market vs Views
                  </Typography>
                  <Typography variant="small" className="text-gray-500 text-xs mb-3">
                    Listings below the diagonal are underperforming relative to time on market
                  </Typography>
                  <ResponsiveContainer width="100%" height={chartHeight}>
                    <ScatterChart margin={{ bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="x" name="Days on Market" tick={{ fontSize: 11 }} label={{ value: 'Days on Market', position: 'bottom', fontSize: 11, fill: '#9ca3af' }} />
                      <YAxis dataKey="y" name="Total Views" tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                      <ZAxis dataKey="impressions" range={[40, 400]} />
                      <Tooltip content={<ScatterTooltip />} />
                      <Scatter data={scatterData}>
                        {scatterData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            )}

            {/* Chart 4: Views by Model (donut) */}
            {modelPieData.length > 1 && (
              <Card className="shadow-sm">
                <CardBody className="p-4">
                  <Typography variant="small" className="font-semibold text-gray-700 mb-1">
                    Views Distribution by Model
                  </Typography>
                  <Typography variant="small" className="text-gray-500 text-xs mb-3">
                    Where buyer attention is concentrated across models
                  </Typography>
                  <ResponsiveContainer width="100%" height={chartHeight}>
                    <PieChart>
                      <Pie
                        data={modelPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={85}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={{ stroke: '#9ca3af', strokeWidth: 1 }}
                      >
                        {modelPieData.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            )}

            {/* Chart 5: Engagement by Listing (stacked bar) */}
            {engagementData.length > 0 && (
              <Card className="shadow-sm lg:col-span-2">
                <CardBody className="p-4">
                  <Typography variant="small" className="font-semibold text-gray-700 mb-1">
                    Lead Engagement by Listing
                  </Typography>
                  <Typography variant="small" className="text-gray-500 text-xs mb-3">
                    Calls, emails, and watchlist adds — strongest signals of buyer intent
                  </Typography>
                  <ResponsiveContainer width="100%" height={Math.max(180, engagementData.length * 28)}>
                    <BarChart data={engagementData} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} interval={0} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="Calls" fill="#059669" stackId="a" />
                      <Bar dataKey="Emails" fill="#0891b2" stackId="a" />
                      <Bar dataKey="Watchlists" fill="#d946ef" stackId="a" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            )}
            {/* Chart 6: DOM vs Model Benchmark */}
            {domBenchmarkData.length > 0 && (
              <Card className="shadow-sm lg:col-span-2">
                <CardBody className="p-4">
                  <Typography variant="small" className="font-semibold text-gray-700 mb-1">
                    Days on Market vs Model Average
                  </Typography>
                  <Typography variant="small" className="text-gray-500 text-xs mb-3">
                    Active listings compared to historical average sale time — red bars are overdue
                  </Typography>
                  <ResponsiveContainer width="100%" height={Math.max(180, domBenchmarkData.length * 32)}>
                    <BarChart data={domBenchmarkData} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis type="number" tick={{ fontSize: 11 }} label={{ value: 'Days', position: 'bottom', fontSize: 11, fill: '#9ca3af' }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} interval={0} />
                      <Tooltip content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0]?.payload;
                        return (
                          <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-3 text-sm">
                            <div className="font-semibold text-gray-700">{label}</div>
                            <div className="text-gray-500 text-xs mb-1">{d?.model}</div>
                            {payload.map((entry, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color || entry.fill }} />
                                <span className="text-gray-500">{entry.name}:</span>
                                <span className="font-medium">{entry.value}d</span>
                              </div>
                            ))}
                            {d?.delta != null && (
                              <div className={`mt-1 text-xs font-medium ${d.delta > 30 ? 'text-red-600' : d.delta > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                {d.delta > 0 ? '+' : ''}{d.delta}d vs average ({d['Sales Count']} sales)
                              </div>
                            )}
                          </div>
                        );
                      }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="Days on Market" radius={[0, 4, 4, 0]}>
                        {domBenchmarkData.map((entry, i) => (
                          <Cell key={i} fill={entry.delta > 30 ? '#ef4444' : entry.delta > 0 ? '#f59e0b' : '#22c55e'} />
                        ))}
                      </Bar>
                      <Bar dataKey="Avg DOM (Model)" fill="#e5e7eb" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            )}

            {/* Chart 7: Lead Engagement by Model */}
            {engagementByModelData.length > 0 && (
              <Card className="shadow-sm lg:col-span-2">
                <CardBody className="p-4">
                  <Typography variant="small" className="font-semibold text-gray-700 mb-1">
                    Lead Engagement by Model
                  </Typography>
                  <Typography variant="small" className="text-gray-500 text-xs mb-3">
                    Aggregate calls, emails, and watchlists per model — shows which models generate the most buyer activity
                  </Typography>
                  <ResponsiveContainer width="100%" height={Math.max(180, engagementByModelData.length * 40)}>
                    <BarChart data={engagementByModelData} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={140} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="Calls" fill="#059669" stackId="a" />
                      <Bar dataKey="Emails" fill="#0891b2" stackId="a" />
                      <Bar dataKey="Watchlists" fill="#d946ef" stackId="a" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default WebStatsCharts;
